import React, { useEffect, useState, useMemo } from 'react';
import 'moment-timezone';
import {
  numeroSegonsEntreDates,
  numeroSegonsEntreDates2,
  operacionsRestaHores,
  convertirMilisHores,
  convertirMilisMinuts,
  convertirSegonsHores,
  convertirSegonsMinuts
} from '../../Helpers/funcionsDates';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';

// ARQUITECTURA NOVA: llegeix de cp.detallSetmana (endpoint unificat /setmana/actual/detall)
// en lloc de cp.llistatFixatgesSetmana + cp.llistatIncidenciesSetmana
//
// CORRECCIONS de bugs originals (mantingudes):
// 1. Doble suma d'incidències eliminada (computMinuts ja NO s'afegeix al total global)
// 2. Bucle de fixatges ara itera i+=2 (entrada+sortida) en lloc de fixatges[index+1]

export const ResumTotalHoresSetmana = ({ hores }) => {
  const { configUsuari, cp } = useSelector(state => state);
  const { contingut } = configUsuari;
  const [contadorJornadaIniciada, setContadorJornadaIniciada] = useState(new Date());
  const { t } = useTranslation('generic');

  // Interval fix (deps buit -> creat una sola vegada)
  useEffect(() => {
    const interval = setInterval(() => setContadorJornadaIniciada(new Date()), 10000);
    return () => clearInterval(interval);
  }, []);

  const totals = useMemo(() => {
    const detallSetmana = cp?.detallSetmana;
    if (!detallSetmana?.fixatges_setmanals) return null;

    let segonsTotalSetmana = 0;
    let segonsTeletreball = 0;
    let segonsPresencial = 0;
    let minutsBossaHores = 0;

    // ── Borsa d'hores de la setmana ──────────────────────────────────────────
    if (contingut) {
      contingut.llistatDiesBorsaHores.diesBorsa.forEach(e => {
        if (
          e.estat === '1' &&
          e.dataBorsa >= detallSetmana.dia_inici_setmana &&
          e.dataBorsa <= detallSetmana.dia_final_setmana
        ) {
          minutsBossaHores += e.minutsDemanats * 60 * 1000;
        }
      });
      segonsTotalSetmana += minutsBossaHores;
    }

    // ── Incidències justificades de la setmana ───────────────────────────────
    // CORRECCIO: les incidències s'afegeixen UNA SOLA VEGADA aquí.
    // NO s'usa computMinuts (evita el doble comptatge de la versió original).
    if (detallSetmana.llistatIncidencies?.length) {
      detallSetmana.llistatIncidencies.forEach(e => {
        if (e.contahores_Incidencia !== '1') return;
        if (e.Justificat_IncidenciaFix !== '1') return;

        const iniciInci = moment(e.DataInici_IncidenciaFix).format('YYYY-MM-DD');
        const fiInci    = moment(e.DataFi_IncidenciaFix).format('YYYY-MM-DD');
        if (fiInci < detallSetmana.dia_inici_setmana || iniciInci > detallSetmana.dia_final_setmana) return;

        const limitDiari = (hores / 5) * 60 * 60 * 1000;
        const durada = numeroSegonsEntreDates(e.DataInici_IncidenciaFix, e.DataFi_IncidenciaFix);

        if ([1, 4, 12].includes(Number(e.IdTipusIncidencia_IncidenciaFix))) {
          segonsTotalSetmana += durada;
        } else {
          segonsTotalSetmana += Math.min(durada, limitDiari);
        }
      });
    }

    // ── Fixatges de la setmana ───────────────────────────────────────────────
    // CORRECCIO: iteració de 2 en 2 (entrada[i] + sortida[i+1])
    detallSetmana.fixatges_setmanals.forEach(diaObj => {
      const fixatges = diaObj.fixatges;
      if (!fixatges?.length) return;

      for (let i = 0; i < fixatges.length; i += 2) {
        const entrada = fixatges[i];
        const sortida = fixatges[i + 1];

        if (!['1', '2'].includes(entrada.IdTipus_Fixatge)) continue;

        let segons;
        if (sortida) {
          segons = numeroSegonsEntreDates(entrada.DataHora_Fixatge, sortida.DataHora_Fixatge);
        } else {
          const esAvui = moment(entrada.DataHora_Fixatge).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
          if (!esAvui) continue;
          segons = numeroSegonsEntreDates2(entrada.DataHora_Fixatge, contadorJornadaIniciada);
        }

        segonsTotalSetmana += segons;
        if (entrada.IdTipus_Fixatge === '2') segonsTeletreball += segons;
        if (entrada.IdTipus_Fixatge === '1') segonsPresencial += segons;
      }
    });

    let segonsRestants = operacionsRestaHores(segonsTotalSetmana, hores);
    const deMes = segonsRestants < 0;
    const computSegonsDeMes = deMes ? -segonsRestants : 0;
    if (deMes) segonsRestants = 0;

    return {
      horesTreballades:          convertirMilisHores(segonsTotalSetmana),
      minutsTreballats:          convertirMilisMinuts(segonsTotalSetmana),
      horesTeletreball:          convertirMilisHores(segonsTeletreball),
      minutsTeletreball:         convertirMilisMinuts(segonsTeletreball),
      horesPresencial:           convertirMilisHores(segonsPresencial),
      minutsPresencial:          convertirMilisMinuts(segonsPresencial),
      horesBossaHores:           convertirMilisHores(minutsBossaHores),
      minutBossaHores:           convertirMilisMinuts(minutsBossaHores),
      horesRestants:             parseInt(segonsRestants / (60 * 60)),
      minutsRestants:            parseInt((segonsRestants / 60) % 60),
      horesDesMes:               convertirSegonsHores(computSegonsDeMes),
      minutsDeMes:               convertirSegonsMinuts(computSegonsDeMes),
      deMes
    };
  }, [cp?.detallSetmana, contadorJornadaIniciada, configUsuari, hores]);

  if (!totals) return null;

  return (
    <div>
      {totals.horesTeletreball > 0 || totals.minutsTeletreball > 0 ? (
        <p>
          <strong>{t('ResumHores.teletreball')}</strong>{' '}
          {totals.horesTeletreball} {t('Generics.hores')} {t('Generics.caracIoY')} {totals.minutsTeletreball} {t('Generics.minuts')}
        </p>
      ) : null}
      {totals.horesPresencial > 0 || totals.minutsPresencial > 0 ? (
        <p>
          <strong>{t('ResumHores.presencial')}</strong>{' '}
          {totals.horesPresencial} {t('Generics.hores')} {t('Generics.caracIoY')} {totals.minutsPresencial} {t('Generics.minuts')}
        </p>
      ) : null}
      {totals.horesBossaHores > 0 || totals.minutBossaHores > 0 ? (
        <p>
          <strong>{t('ResumHores.bossaHores')}</strong>{' '}
          {totals.horesBossaHores} {t('Generics.hores')} {t('Generics.caracIoY')} {totals.minutBossaHores} {t('Generics.minuts')}
        </p>
      ) : null}
      {totals.horesTreballades > 0 || totals.minutsTreballats > 0 ? (
        <p>
          <strong>{t('ResumHores.horesTreball')}</strong>{' '}
          {totals.horesTreballades} {t('Generics.hores')} {t('Generics.caracIoY')} {totals.minutsTreballats} {t('Generics.minuts')}
        </p>
      ) : null}
      {totals.horesRestants > 0 || totals.minutsRestants > 0 ? (
        <p>
          <strong>{t('ResumHores.restants')}</strong>{' '}
          {totals.horesRestants} {t('Generics.hores')} {t('Generics.caracIoY')} {totals.minutsRestants} {t('Generics.minuts')}
        </p>
      ) : null}
    </div>
  );
};

export default React.memo(ResumTotalHoresSetmana);